/** Minimal shape of the GET /users/me record used to identify the user. */
export type CurrentUserRecord = {
  id: string | number;
  name?: string | null;
  email?: string | null;
};

/** Minimal shape of the Microsoft Graph /me profile. */
export type GraphProfileLike = {
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
};

/** Minimal shape of an entry in the reference `users` list. */
export type UserListEntry = {
  id: string | number;
  email?: string | null;
};

function normalizeEmail(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

/**
 * Fallback only: match the Graph profile to the users list by mail, then by
 * userPrincipalName, ignoring case and surrounding whitespace.
 */
export function findUserByGraphProfile<T extends UserListEntry>(
  profile: GraphProfileLike | null | undefined,
  users: readonly T[],
): T | undefined {
  if (!profile) {
    return undefined;
  }

  const candidates = [profile.mail, profile.userPrincipalName]
    .map(normalizeEmail)
    .filter((candidate) => candidate !== "");

  for (const candidate of candidates) {
    const match = users.find((user) => normalizeEmail(user.email) === candidate);
    if (match) {
      return match;
    }
  }

  return undefined;
}

/**
 * Internal (10fifty) user id: from GET /users/me when available, which works
 * for MSAL and local password login alike; otherwise from the Graph profile.
 */
export function resolveTenfiftyId({
  currentUser,
  profile,
  users,
}: {
  currentUser: CurrentUserRecord | null | undefined;
  profile: GraphProfileLike | null | undefined;
  users: readonly UserListEntry[];
}): string | number | undefined {
  if (currentUser && currentUser.id != null) {
    return currentUser.id;
  }

  return findUserByGraphProfile(profile, users)?.id;
}

/**
 * The `user` object exposed by GlobalContext: the Graph profile fields (when
 * signed in with MSAL) plus `displayName` and `tenfiftyid`. Returns null when
 * neither the API user record nor a Graph profile is available.
 */
export function buildGlobalUser<P extends GraphProfileLike>({
  currentUser,
  profile,
  users,
}: {
  currentUser: CurrentUserRecord | null | undefined;
  profile: P | null | undefined;
  users: readonly UserListEntry[];
}) {
  if (!currentUser && !profile) {
    return null;
  }

  return {
    ...profile,
    displayName:
      profile?.displayName || currentUser?.name || currentUser?.email || "",
    mail: profile?.mail || currentUser?.email || "",
    tenfiftyid: resolveTenfiftyId({ currentUser, profile, users }),
  };
}
